// Runtime renderer overrides — read from the URL query string at startup.
//
// Designed for the "I just trained my first splat, does it render?" smoke
// test before the operator commits the splat URL into rooms.json. Two forms:
//
//   ?splat=living_room:/uploads/living-test.ply   → that one room, that URL
//   ?splat=/uploads/living-test.ply               → the default room (no id prefix)
//   ?splat=https://example.com/test.ply           → URL with protocol works in either form
//
// Activated only if the parameter is present, so production loads keep their
// rooms.json-declared renderers. The override survives room changes within
// the same browser session (URL params persist), so the operator can navigate
// out and back in without losing the override.
//
// Future: same pattern can carry ?gltf=... for the Sprint 2B scaffold.

import type { Room, Variant } from '../app/types';
import type { RendererKind } from './roomRenderer';

interface ParsedOverride {
  /** Room id to override. null = "apply to whichever room the user's currently in". */
  roomId: string | null;
  url: string;
}

/**
 * Shared parser for `?<key>=<roomId>:<url>` query params. Handles the
 * absolute-URL case (`https://…`) where colons in the URL itself must NOT
 * be treated as the roomId separator.
 */
function parseOverrideParam(paramName: string, kindLabel: string): ParsedOverride | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get(paramName);
  if (!raw) return null;

  let roomId: string | null = null;
  let url = raw;
  const hasProtocol = /^[a-zA-Z]+:\/\//.test(raw);
  if (!hasProtocol) {
    const sep = raw.indexOf(':');
    if (sep > 0) {
      roomId = raw.slice(0, sep);
      url = raw.slice(sep + 1);
    }
  }

  if (!url) {
    console.warn(`[renderer-overrides] ignored ?${paramName} param with empty URL: ${raw}`);
    return null;
  }

  console.info(
    `[renderer-overrides] ${kindLabel} override active${roomId ? ` for room "${roomId}"` : ' (any room)'}: ${url}`,
  );
  return { roomId, url };
}

let cachedSplat: ParsedOverride | null | undefined;
let cachedGltf: ParsedOverride | null | undefined;

function readSplatParam(): ParsedOverride | null {
  if (cachedSplat === undefined) cachedSplat = parseOverrideParam('splat', 'gsplat');
  return cachedSplat;
}

function readGltfParam(): ParsedOverride | null {
  if (cachedGltf === undefined) cachedGltf = parseOverrideParam('gltf', 'gltf');
  return cachedGltf;
}

function overrideMatchesRoom(override: ParsedOverride | null, roomId: string): boolean {
  return override !== null && (override.roomId === null || override.roomId === roomId);
}

/**
 * Resolve which renderer to use for this room+variant. URL overrides win:
 * ?splat= forces 'gsplat', ?gltf= forces 'gltf' (?splat= beats ?gltf= if
 * both are set, which would be a configuration mistake anyway). Otherwise
 * falls back to the variant's renderer, then the room's, then 'panorama'.
 */
export function resolveRendererKind(room: Room, variant: Variant): RendererKind {
  if (overrideMatchesRoom(readSplatParam(), room.id)) return 'gsplat';
  if (overrideMatchesRoom(readGltfParam(), room.id)) return 'gltf';
  return variant.renderer ?? room.renderer ?? 'panorama';
}

/**
 * Resolve which splat URL to load. Override beats variant.splatUrl beats
 * room.splatUrl. Returns null if no gsplat asset is configured.
 */
export function resolveSplatUrl(room: Room, variant: Variant): string | null {
  const splat = readSplatParam();
  if (overrideMatchesRoom(splat, room.id)) return splat!.url;
  return variant.splatUrl ?? room.splatUrl ?? null;
}

/**
 * Resolve which glTF model URL to load. Override beats variant.modelUrl
 * beats room.modelUrl. Returns null if no glTF asset is configured.
 */
export function resolveModelUrl(room: Room, variant: Variant): string | null {
  const gltf = readGltfParam();
  if (overrideMatchesRoom(gltf, room.id)) return gltf!.url;
  return variant.modelUrl ?? room.modelUrl ?? null;
}

/** Visible only for tests / debugging — drops caches so a hot reload re-reads the URL. */
export function _resetOverrideCacheForTests(): void {
  cachedSplat = undefined;
  cachedGltf = undefined;
}
